import React, { useEffect, useState } from "react";
import { Input, Button, message, Steps, Select, Form, InputNumber } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { getAuthReducer } from "../../../../redux/Reducer/authReducer";
import { debounce } from 'lodash';
import { searchCustomers } from "../../../../redux/Actions";

const { Option } = Select;

// Utility function to format numbers as currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
  }).format(value);
};

const CreateOrderStep = ({ onFinish, setConfettiActive, confettiActive }) => {
  const {
    authUser,
    allApplicant,
    isloadingAllApplicant,
    // isSearched,
  } = useSelector(getAuthReducer);

  const [current, setCurrent] = useState(0);
  const dispatch = useDispatch();
  const [orderItems, setOrderItems] = useState([]);
  const [initialProducts, setInitialProducts] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState({
    id: "",
    name: "",
  });
  const [selectedLocation, setSelectedLocation] = useState({
    id: authUser?.user?.location_id,
    name: "",
  });
  const [customerList, setCustomerList] = useState([]);
  const [locationList, setLocationList] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isLoadingCreateOrder, setIsLoadingCreateOrder] = useState(false);

  // Search related states
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    if (confettiActive === true) {
      setCurrent(0);
    }
  }, [confettiActive, current]);

  // Watch for search results from Redux
  useEffect(() => {
    if (allApplicant?.data && hasSearched) {
      // console.log('Search API Response:', allApplicant.data); // Debug log

      // Since this is now a customer search, the data should be customers directly
      const customers = Array.isArray(allApplicant.data)
        ? allApplicant.data.map(customer => ({
          id: customer.id,
          name: customer.name || customer.full_name || customer.first_name + ' ' + customer.last_name || `Customer ${customer.id}`
        }))
        : [];

      // console.log('Processed customers:', customers); // Debug log
      setSearchResults(customers);
      setIsSearching(false);
    }
  }, [allApplicant, hasSearched]);

  // Watch for loading state changes
  useEffect(() => {
    if (!isloadingAllApplicant && hasSearched) {
      setIsSearching(false);
    }
  }, [isloadingAllApplicant, hasSearched]);

  const fetchProducts = async () => {
    try {
      const bearerToken = authUser.access_token;
      const response = await fetch(
        `${window.location.hostname === "localhost"
          ? process.env.REACT_APP_API_BASE_LOCAL_URL
          : process.env.REACT_APP_API_BASE_DEV_URL}/products`,
        {
          headers: {
            Authorization: `Bearer ${bearerToken}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch products");
      }

      const data = await response.json();
      setInitialProducts(data.data || []);
    } catch (error) {
      // console.error("Error fetching products:", error);
      message.error("Failed to fetch products");
    }
  };

  const fetchCustomers = async () => {
    try {
      const bearerToken = authUser.access_token;
      const response = await fetch(
        `${window.location.hostname === "localhost"
          ? process.env.REACT_APP_API_BASE_LOCAL_URL
          : process.env.REACT_APP_API_BASE_DEV_URL}/users?user_type_id=3&status=1`,
        {
          headers: {
            Authorization: `Bearer ${bearerToken}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch customers");
      }

      const data = await response.json();
      setCustomerList(data.data || []);
    } catch (error) {
      // console.error("Error fetching customers:", error);
      message.error("Failed to fetch customers");
    }
  };

  const fetchLocations = async () => {
    try {
      const bearerToken = authUser.access_token;
      const response = await fetch(
        `${window.location.hostname === "localhost"
          ? process.env.REACT_APP_API_BASE_LOCAL_URL
          : process.env.REACT_APP_API_BASE_DEV_URL}/locations`,
        {
          headers: {
            Authorization: `Bearer ${bearerToken}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch locations");
      }

      const data = await response.json();
      setLocationList(data.data || []);
    } catch (error) {
      // console.error("Error fetching locations:", error);
      message.error("Failed to fetch locations");
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchCustomers();
    fetchLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search function
  const debouncedSearch = debounce(async (searchValue) => {
    if (!searchValue || searchValue.trim() === "") {
      setSearchResults([]);
      setHasSearched(false);
      setIsSearching(false);
      return;
    }

    // console.log('Searching for:', searchValue.trim()); // Debug log

    try {
      setIsSearching(true);
      setHasSearched(true);

      const payload = { searchQuery: searchValue.trim() };
      await dispatch(searchCustomers(payload));

    } catch (error) {
      // console.error("Error searching customers:", error);
      message.error("Failed to search customers");
      setIsSearching(false);
      setHasSearched(false);
    }
  }, 300);

  const handleCustomerSearch = (searchValue) => {
    debouncedSearch(searchValue);
  };

  const handleCustomerChange = (value) => {
    // First try to find in search results, then in customer list
    const customer = searchResults.find((cus) => cus.id === value) ||
      customerList.find((cus) => cus.id === value);

    if (customer) {
      setSelectedCustomer({
        id: customer.id,
        name: customer.name,
      });
    }
  };

  const handleLocationChange = (value) => {
    const location = locationList.find((loc) => loc.id === value);
    if (location) {
      setSelectedLocation({
        id: location.id,
        name: location.address,
      });
    }
  };

  // Get customer options based on search state
  const getCustomerOptions = () => {
    if (hasSearched && searchResults.length > 0) {
      return searchResults;
    }
    return customerList;
  };

  const next = () => setCurrent(current + 1);
  const prev = () => setCurrent(current - 1);

  const handleFinish = async () => {
    if (!selectedCustomer.id || !selectedLocation.id || orderItems.length === 0) {
      message.error("Please complete all required fields");
      return;
    }

    setIsLoadingCreateOrder(true);

    const payload = {
      user_id: selectedCustomer.id,
      location_id: selectedLocation.id,
      is_internal_order: true,
      is_approved: false,
      items: orderItems.map((item) => ({
        product_id: item.id,
        qty: item.quantity,
      })),
    };

    try {
      const bearerToken = authUser.access_token;
      const response = await fetch(
        `${window.location.hostname === "localhost"
          ? process.env.REACT_APP_API_BASE_LOCAL_URL
          : process.env.REACT_APP_API_BASE_DEV_URL}/orders`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${bearerToken}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const responseData = await response.json();

      if (responseData?.status === true) {
        message.success("Order created successfully!");

        // Reset form
        setOrderItems([]);
        setSelectedCustomer({ id: "", name: "" });
        setSelectedLocation({ id: authUser?.user?.location_id, name: "" });
        setSearchResults([]);
        setHasSearched(false);
        setConfettiActive(true);
        setCurrent(0);

        onFinish();
      } else {
        message.error(responseData?.message || "Failed to create order. Please try again!");
      }
    } catch (error) {
      // console.error("Failed to submit order:", error);
      message.error("Failed to submit order. Please try again");
    } finally {
      setIsLoadingCreateOrder(false);
    }
  };

  const handleAddProduct = () => {
    if (selectedProduct && quantity > 0) {
      const newItem = {
        ...selectedProduct,
        quantity,
        subtotal: Number(selectedProduct?.amount || 0) * quantity,
      };
      setOrderItems([...orderItems, newItem]);
      setSelectedProduct(null);
      setQuantity(1);
    } else {
      message.warning("Please select a product and quantity");
    }
  };

  const handleProductSelect = (productId) => {
    const product = initialProducts?.find((product) => product.id === productId);
    setSelectedProduct(product);
  };

  const handleQuantityChange = (newQuantity, index) => {
    if (newQuantity < 1) return;

    const updatedItems = orderItems?.map((item, idx) => {
      if (idx === index) {
        return {
          ...item,
          quantity: newQuantity,
          subtotal: Number(item?.amount || 0) * newQuantity,
        };
      }
      return item;
    });
    setOrderItems(updatedItems);
  };

  const handleDeleteProduct = (index) => {
    const newItems = orderItems.filter((_, idx) => idx !== index);
    setOrderItems(newItems);
  };

  const totalAmount = orderItems.reduce((acc, item) => acc + (item.subtotal || 0), 0);

  const steps = [
    {
      title: "Customer",
      content: (
        <div className="row">
          <div className="col mt-4">
            <Form.Item
              label="Select Customer"
              required
              rules={[{ required: true, message: 'Please select a customer' }]}
            >              <Select
              showSearch
              placeholder="Search and select a customer"
              value={selectedCustomer.id || undefined}
              onChange={handleCustomerChange}
              onSearch={handleCustomerSearch}
              optionFilterProp="children"
              placement="bottomLeft"
              notFoundContent={
                isSearching
                  ? "Searching for customers..."
                  : hasSearched && searchResults.length === 0
                    ? "No customers found for this search"
                    : getCustomerOptions().length === 0
                      ? "No customers available"
                      : null
              }
              loading={isSearching}
              filterOption={false} // Disable local filtering since we're doing server-side search
              // dropdownRender={(menu) => (
              //   <div>
              //     {isSearching && (
              //       <div style={{ padding: '8px 12px', color: '#999', fontStyle: 'italic' }}>
              //         Searching for customers...
              //       </div>
              //     )}
              //     {menu}
              //   </div>
              // )}
            >
                {getCustomerOptions()
                  .filter(customer => customer?.id && customer?.name) // Only show valid customers
                  .map((customer) => (
                    <Option key={customer.id} value={customer.id}>
                      {customer.name}
                    </Option>
                  ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Select Location"
              required
              rules={[{ required: true, message: 'Please select a location' }]}
            >              <Select
              value={selectedLocation?.id || undefined}
              onChange={handleLocationChange}
              placeholder="Select a location"
              placement="bottomLeft"
              disabled={authUser?.user?.roles?.name !== "Super Admin"}
            >
                {locationList.map((location) => (
                  <Option key={location?.id} value={location?.id}>
                    {location?.name} - {location?.address}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        </div>
      ),
    },
    {
      title: "Product",
      content: (
        <div className="mt-4">
          <h6>Choose product and quantity</h6>
          <div className="border rounded-2 py-2">
            <div className="row bg-light mx-1">
              <div className="col-5 fw-bold">PRODUCT</div>
              <div className="col-2 fw-bold">QUANTITY</div>
              <div className="col-2 fw-bold">PRICE</div>
              <div className="col-2 fw-bold">SUBTOTAL</div>
              <div className="col-1 fw-bold"></div>
            </div>

            {orderItems.map((item, index) => (
              <div className="row mx-1 mt-3" key={`${item.id}-${index}`}>
                <div className="col-5">
                  <div className="row">
                    <div className="col-2">
                      <img
                        src={item?.images?.[0]?.url || '/placeholder-image.png'}
                        width={40}
                        height={35}
                        className="border border-1 rounded-2"
                        alt="product"
                        onError={(e) => {
                          e.target.src = '/placeholder-image.png';
                        }}
                      />
                    </div>
                    <div className="col-10">{item?.name}</div>
                  </div>
                </div>
                <div className="col-2">
                  <InputNumber
                    min={1}
                    value={item?.quantity}
                    onChange={(value) => handleQuantityChange(value, index)}
                  />
                </div>
                <div className="col-2">{formatCurrency(item?.amount || 0)}</div>
                <div className="col-2">{formatCurrency(item?.subtotal || 0)}</div>
                <div className="col-1">
                  <span
                    className="text-danger mx-1 cursor-pointer"
                    onClick={() => handleDeleteProduct(index)}
                    style={{ cursor: 'pointer' }}
                  >
                    <DeleteOutlined />
                  </span>
                </div>
              </div>
            ))}

            <hr className="mb-3" />

            <div className="row mx-1">
              <div className="col-6">
                <Form.Item>
                  <Select
                    value={selectedProduct?.id || undefined}
                    onChange={handleProductSelect}
                    placeholder="Select a product"
                  >
                    {initialProducts.map((product) => (
                      <Option key={product?.id} value={product?.id}>
                        {product?.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>
              <div className="col-2">
                <Form.Item>
                  <InputNumber
                    min={1}
                    value={quantity}
                    onChange={setQuantity}
                  />
                </Form.Item>
              </div>
              <div className="col-2">
                {formatCurrency(Number(selectedProduct?.amount || 0))}
              </div>
              <div className="col-2">
                {formatCurrency(Number(selectedProduct?.amount || 0) * quantity)}
              </div>
            </div>

            <div className="d-flex justify-content-end">
              <button
                className="mx-2 mb-3 btn"
                style={{ backgroundColor: "#131523", color: "white" }}
                onClick={handleAddProduct}
                disabled={!selectedProduct}
              >
                Add Product <PlusOutlined />
              </button>
            </div>
          </div>

          <div className="d-flex justify-content-end my-5">
            <span>
              TOTAL AMOUNT:{" "}
              <span className="fw-bold">
                {formatCurrency(totalAmount)}
              </span>
            </span>
          </div>

          <div className="d-flex justify-content-start my-5">
            <Form.Item label="Apply discount/Promos" style={{ width: 600 }}>
              <Input placeholder="Input percentage (%) or amount (₦) if applicable" />
            </Form.Item>
          </div>
        </div>
      ),
    },
    {
      title: "Review Order",
      content: (
        <div>
          <div className="row">
            <div className="col mt-4">
              <Form.Item label="Customer">
                <Input value={selectedCustomer?.name} disabled />
              </Form.Item>

              <Form.Item label="Location">
                <Input value={selectedLocation?.name} disabled />
              </Form.Item>
            </div>
          </div>

          <div className="mt-4">
            <h6>Product details</h6>
            <div className="border rounded-2 py-2">
              <div className="row bg-light mx-1">
                <div className="col-6 fw-bold">PRODUCT</div>
                <div className="col-2 fw-bold">QUANTITY</div>
                <div className="col-2 fw-bold">PRICE</div>
                <div className="col-2 fw-bold">SUBTOTAL</div>
              </div>

              {orderItems.map((item, index) => (
                <div className="row mx-1 mt-3" key={`review-${item.id}-${index}`}>
                  <div className="col-6">
                    <div className="row">
                      <div className="col-2">
                        <img
                          src={item?.images?.[0]?.url || '/placeholder-image.png'}
                          width={40}
                          height={35}
                          className="border border-1 rounded-2"
                          alt="product"
                          onError={(e) => {
                            e.target.src = '/placeholder-image.png';
                          }}
                        />
                      </div>
                      <div className="col-10">{item?.name}</div>
                    </div>
                  </div>
                  <div className="col-2">x{item?.quantity}</div>
                  <div className="col-2">{formatCurrency(item?.amount || 0)}</div>
                  <div className="col-2">{formatCurrency(item?.subtotal || 0)}</div>
                </div>
              ))}

              <div
                className="mt-4 d-flex justify-content-end"
                style={{
                  backgroundColor: "#fffff4",
                  border: "1px solid #ddd",
                  borderRadius: "4px"
                }}
              >
                <div className="m-2 mx-3">
                  <div className="mb-1">
                    Subtotal:{" "}
                    <span style={{ marginLeft: "57px" }}>
                      {formatCurrency(totalAmount)}
                    </span>
                  </div>
                  <div className="mb-1">
                    Discount: <span style={{ marginLeft: "55px" }}>0%</span>
                  </div>
                  <div>
                    Total Amount{" "}
                    <span className="fw-bold" style={{ marginLeft: "30px" }}>
                      {formatCurrency(totalAmount)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <Steps
        current={current}
        items={steps.map((step) => ({ key: step.title, title: step.title }))}
      />
      <div className="mt-4">{steps[current].content}</div>
      <div className="d-flex justify-content-end" style={{ marginTop: 24 }}>
        {current > 0 && (
          <Button onClick={prev} className="mx-3">
            Back
          </Button>
        )}
        {current < steps.length - 1 && (
          <Button
            type="primary"
            onClick={next}
            disabled={
              (current === 0 && (!selectedCustomer.id || !selectedLocation.id)) ||
              (current === 1 && orderItems.length === 0)
            }
          >
            Continue
          </Button>
        )}
        {current === steps.length - 1 && (
          <Button
            type="primary"
            onClick={handleFinish}
            loading={isLoadingCreateOrder}
            disabled={!selectedCustomer.id || !selectedLocation.id || orderItems.length === 0}
          >
            {isLoadingCreateOrder ? 'Creating Order...' : 'Create Order'}
          </Button>
        )}
      </div>
    </>
  );
};

const CreateAdminOrder = (props) => {
  const { setConfettiActive, confettiActive } = props;

  const usermanText = {
    width: "189px",
    flexShrink: 0,
    color: "#45464E",
    fontFamily: '"MADE TOMMY"',
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "28px",
  };

  return (
    <div className="p-3 mt-5" style={{ borderRadius: "0px", margin: "0px" }}>
      <div className="row">
        <div className="d-flex justify-content-start">
          <div className="col">
            <span style={usermanText}>Orders &gt; Create Order</span>
          </div>
        </div>
      </div>

      <div className="p-2 mt-5">
        <Form layout="vertical">
          <CreateOrderStep
            setConfettiActive={setConfettiActive}
            confettiActive={confettiActive}
            onFinish={() => { }}
          />
        </Form>
      </div>
    </div>
  );
};

export default CreateAdminOrder;